import { useTranslation } from "react-i18next";import React, { useState } from 'react';
import {
  User, Plus, Search, Filter, Phone, Mail, Star, MapPin,
  RefreshCw, Edit, Eye, X, Truck, Award, Calendar, Shield } from
'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { useDrivers, useFleetAPI } from '../hooks/useFleetData';

const statusConfig = {
  available: { color: 'bg-emerald-500', label: 'Available' },
  on_duty: { color: 'bg-blue-500', label: 'On Duty' },
  off_duty: { color: 'bg-gray-400', label: 'Off Duty' },
  on_break: { color: 'bg-amber-500', label: 'On Break' },
  unavailable: { color: 'bg-red-500', label: 'Unavailable' },
  suspended: { color: 'bg-red-700', label: 'Suspended' }
};

const licenseLabels = {
  A: 'Motorcycle',
  B: 'Car/Light',
  C: 'Truck',
  D: 'Bus/Passenger',
  E: 'Articulated',
  AB: 'A + B',
  AC: 'A + C',
  AD: 'A + D',
  AE: 'A + E'
};

const DriversPage = () => {const { t } = useTranslation("fleet");
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  const { drivers, loading, refresh } = useDrivers(filters);
  const { fetchAPI } = useFleetAPI();

  const filteredDrivers = drivers.filter((d) =>
  d.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  d.driver_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  d.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group by status for stats
  const statusCounts = drivers.reduce((acc, d) => {
    acc[d.status] = (acc[d.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("driverManagement", "Driver Management")}</h1>
          <p className="text-gray-500">{t("manageDriversLicenses", "Manage drivers, licenses, and performance")}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw className="w-4 h-4 mr-2" />{t("refresh", "Refresh")}

          </Button>
          <Button
            className="bg-red-500 hover:bg-red-600 text-white"
            size="sm"
            onClick={() => setShowAddModal(true)}>
            
            <Plus className="w-4 h-4 mr-2" />{t("addDriver", "Add Driver")}

          </Button>
        </div>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-white border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => setFilters({})}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium">{t("all", "All")}</p>
                <p className="text-2xl font-bold text-gray-900">{drivers.length}</p>
              </div>
              <User className="w-8 h-8 text-gray-300" />
            </div>
          </CardContent>
        </Card>
        
        {Object.entries(statusConfig).slice(0, 5).map(([status, config]) =>
        <Card
          key={status}
          className={`bg-white border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${
          filters.status === status ? 'ring-2 ring-red-500' : ''}`
          }
          onClick={() => setFilters((f) => ({ ...f, status: f.status === status ? undefined : status }))}>
          
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium">{config.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{statusCounts[status] || 0}</p>
                </div>
                <div className={`w-8 h-8 ${config.color} rounded-lg flex items-center justify-center`}>
                  <User className="w-4 h-4 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Search */}
      <Card className="bg-white border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by name, code, email..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} />
              
            </div>
            <div className="flex items-center gap-2">
              <select
                className="border rounded-lg p-2 text-sm"
                value={filters.license_class || ''}
                onChange={(e) => setFilters((f) => ({ ...f, license_class: e.target.value || undefined }))}>
                
                <option value="">{t("allLicenses", "All Licenses")}</option>
                {Object.entries(licenseLabels).map(([value, label]) =>
                <option key={value} value={value}>{value} - {label}</option>
                )}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Drivers Grid */}
      {loading ?
      <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
        </div> :
      filteredDrivers.length === 0 ?
      <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t("noDriversFound", "No drivers found")}</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || Object.keys(filters).length > 0 ?
            'Try adjusting your search or filters' :
            'Get started by adding your first driver'
            }
            </p>
            <Button
            className="bg-red-500 hover:bg-red-600 text-white"
            onClick={() => setShowAddModal(true)}>
            
              <Plus className="w-4 h-4 mr-2" />{t("addDriver", "Add Driver")}

          </Button>
          </CardContent>
        </Card> :

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredDrivers.map((driver) =>
        <DriverCard
          key={driver.id}
          driver={driver}
          onClick={() => setSelectedDriver(driver)} />

        )}
        </div>
      }

      {/* Add Driver Modal */}
      {showAddModal &&
      <AddDriverModal
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          refresh();
        }} />

      }

      {/* Driver Detail Modal */}
      {selectedDriver &&
      <DriverDetailModal
        driver={selectedDriver}
        onClose={() => setSelectedDriver(null)} />

      }
    </div>);

};

// Driver Card Component
const DriverCard = ({ driver, onClick }) => {const { t } = useTranslation("fleet");
  const status = statusConfig[driver.status] || { color: 'bg-gray-500', label: driver.status };

  return (
    <Card
      className="bg-white border-0 shadow-sm hover:shadow-lg transition-all cursor-pointer group"
      onClick={onClick}>
      
      <CardContent className="p-0">
        <div className="p-4">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
              {driver.name?.charAt(0) || 'D'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-bold text-gray-900 truncate">{driver.name}</p>
                <Badge className={`${status.color} text-white text-xs`}>
                  {status.label}
                </Badge>
              </div>
              <p className="text-sm text-gray-500">{driver.driver_code}</p>
            </div>
          </div>
          
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Shield className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">{t("license", "License:")}{driver.license_class}</span>
            </div>
            {driver.phone &&
            <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">{driver.phone}</span>
              </div>
            }
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((i) =>
                <Star
                  key={i}
                  className={`w-4 h-4 ${i <= Math.round(driver.rating || 5) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />

                )}
              </div>
              <span className="text-sm text-gray-500">{driver.rating?.toFixed(1) || '5.0'}</span>
            </div>
          </div>
        </div>
        
        <div className="p-4 border-t bg-gray-50 grid grid-cols-3 gap-2 text-center text-sm">
          <div>
            <p className="font-bold text-gray-900">{driver.total_trips || 0}</p>
            <p className="text-xs text-gray-500">{t("trips", "Trips")}</p>
          </div>
          <div>
            <p className="font-bold text-emerald-600">{driver.on_time_rate?.toFixed(0) || 100}%</p>
            <p className="text-xs text-gray-500">{t("onTime", "On Time")}</p>
          </div>
          <div>
            <p className="font-bold text-blue-600">{driver.safety_score?.toFixed(0) || 100}%</p>
            <p className="text-xs text-gray-500">{t("safety", "Safety")}</p>
          </div>
        </div>
      </CardContent>
    </Card>);

};

// Add Driver Modal
const AddDriverModal = ({ onClose, onSuccess }) => {const { t } = useTranslation("fleet");
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    license_number: '',
    license_class: 'B',
    employment_type: 'employee'
  });
  const [loading, setLoading] = useState(false);
  const { fetchAPI } = useFleetAPI();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetchAPI('/drivers', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      onSuccess();
    } catch (err) {
      console.error('Failed to create driver:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="w-full max-w-lg m-4 bg-white" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <CardTitle>{t("addNewDriver", "Add New Driver")}</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("firstName", "First Name *")}</label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData((f) => ({ ...f, first_name: e.target.value }))}
                  required />
                
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("lastName", "Last Name *")}</label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData((f) => ({ ...f, last_name: e.target.value }))}
                  required />
                
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">{t("email", "Email")}</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))} />
              
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">{t("phone", "Phone")}</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData((f) => ({ ...f, phone: e.target.value }))} />
              
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("licenseNumber", "License Number *")}</label>
                <Input
                  value={formData.license_number}
                  onChange={(e) => setFormData((f) => ({ ...f, license_number: e.target.value }))}
                  required />
                
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("licenseClass", "License Class *")}</label>
                <select
                  className="w-full border rounded-lg p-2"
                  value={formData.license_class}
                  onChange={(e) => setFormData((f) => ({ ...f, license_class: e.target.value }))}
                  required>
                  
                  {Object.entries(licenseLabels).map(([value, label]) =>
                  <option key={value} value={value}>{value} - {label}</option>
                  )}
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">{t("employmentType", "Employment Type")}</label>
              <select
                className="w-full border rounded-lg p-2"
                value={formData.employment_type}
                onChange={(e) => setFormData((f) => ({ ...f, employment_type: e.target.value }))}>
                
                <option value="employee">{t("employee", "Employee")}</option>
                <option value="contractor">{t("contractor", "Contractor")}</option>
                <option value="partner">{t("partner", "Partner")}</option>
              </select>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>{t("cancel", "Cancel")}

              </Button>
              <Button
                type="submit"
                className="bg-red-500 hover:bg-red-600 text-white"
                disabled={loading}>
                
                {loading ?
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> :

                <Plus className="w-4 h-4 mr-2" />
                }{t("addDriver", "Add Driver")}

              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>);

};

// Driver Detail Modal
const DriverDetailModal = ({ driver, onClose }) => {const { t } = useTranslation("fleet");
  const status = statusConfig[driver.status] || { color: 'bg-gray-500', label: driver.status };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="w-full max-w-2xl m-4 bg-white max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                {driver.name?.charAt(0) || 'D'}
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <CardTitle className="text-xl">{driver.name}</CardTitle>
                  <Badge className={`${status.color} text-white`}>
                    {status.label}
                  </Badge>
                </div>
                <p className="text-gray-500">{driver.driver_code}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">{t("email", "Email")}</p>
              <p className="font-medium">{driver.email || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">{t("phone", "Phone")}</p>
              <p className="font-medium">{driver.phone || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">{t("license", "License")}</p>
              <p className="font-medium">{driver.license_class} - {licenseLabels[driver.license_class]}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">{t("licenseExpiry", "License Expiry")}</p>
              <p className="font-medium">
                {driver.license_expiry ?
                new Date(driver.license_expiry).toLocaleDateString() :
                '-'
                }
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">{t("rating", "Rating")}</p>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) =>
                <Star
                  key={i}
                  className={`w-4 h-4 ${i <= Math.round(driver.rating || 5) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />

                )}
                <span className="ml-1 font-medium">{driver.rating?.toFixed(1) || '5.0'}</span>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">{t("totalTrips", "Total Trips")}</p>
              <p className="font-medium">{driver.total_trips || 0}</p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-600">{driver.on_time_rate?.toFixed(0) || 100}%</p>
              <p className="text-xs text-gray-500">{t("ontimeRate", "On-Time Rate")}</p>
            </div>
            <div className="text-center border-x">
              <p className="text-2xl font-bold text-blue-600">{driver.safety_score?.toFixed(0) || 100}%</p>
              <p className="text-xs text-gray-500">{t("safetyScore", "Safety Score")}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{driver.total_trips || 0}</p>
              <p className="text-xs text-gray-500">{t("completedTrips", "Completed Trips")}</p>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t flex justify-end gap-3">
            <Button variant="outline">
              <Edit className="w-4 h-4 mr-2" />{t("edit", "Edit")}

            </Button>
            <Button variant="outline">
              <Phone className="w-4 h-4 mr-2" />{t("call", "Call")}

            </Button>
            <Button className="bg-red-500 hover:bg-red-600 text-white">
              <Truck className="w-4 h-4 mr-2" />{t("assignTrip", "Assign Trip")}

            </Button>
          </div>
        </CardContent>
      </Card>
    </div>);

};

export default DriversPage;